import { getBaseSubPath } from 'utils';
import { CobrandIdentifier, CobrandMap, isCobrand, normalizeCobrandPathParam } from '../types';

/**
 * Extracts cobrand from hostname by matching against CobrandMap host values.
 * Matches keyword as a complete domain segment (e.g., 'carefirst' matches 'carefirst.com'
 * or 'caremanagement.carefirst.com' but NOT 'mycarefirst.com').
 *
 * If multiple cobrand keywords exist in the same hostname, the LEFTMOST (first) match wins:
 * - 'carefirst.careelevate.com' → CareFirst (appears first/left)
 * - 'careelevate.carefirst.com' → CareElevate (appears first/left)
 *
 * @returns CobrandIdentifier if hostname matches a cobrand, undefined otherwise
 */
export const getCobrandFromHostName = (): CobrandIdentifier | undefined => {
  const { hostname } = window.location;
  const domainParts = hostname.toLowerCase().split('.');

  // Find the leftmost (first) domain part that matches a cobrand host keyword
  // Map each domain part to its matching cobrand entry, then find the first match
  const cobrandEntry = domainParts
    .map((domainPart) =>
      Object.values(CobrandMap).find(({ host }: any) => host && host.toLowerCase() === domainPart),
    )
    .find((entry) => entry !== undefined);

  return cobrandEntry?.id;
};

/**
 * Extracts cobrand from URL path parameter.
 * @returns CobrandIdentifier if path contains valid cobrand, undefined otherwise
 */
export const getCobrandFromPathParam = (): CobrandIdentifier | undefined => {
  const pathSegments = window.location.pathname.split('/');
  const baseSubPath = getBaseSubPath();
  const baseIndex = baseSubPath ? pathSegments.indexOf('member-web') : 0;
  return pathSegments[baseIndex + 1] as CobrandIdentifier | undefined;
};

/**
 * Type guard to check if a value is a valid CobrandIdentifier.
 * @param value - Value to check
 * @returns true if value is a valid CobrandIdentifier
 */
export const isValidCobrand = (value: string | undefined): value is CobrandIdentifier => {
  if (!value) return false;
  return Boolean(normalizeCobrandPathParam(value));
};

/**
 * Gets the current cobrand with fallback priority:
 * 1. Hostname-based detection
 * 2. Path parameter detection
 * 3. Default to Wellframe cobrand
 * @returns CobrandIdentifier
 */
export const getCobrandWithDefault = (): CobrandIdentifier => {
  // Priority 1: Check hostname
  const hostCobrand = getCobrandFromHostName();
  if (hostCobrand) {
    return hostCobrand;
  }

  // Priority 2: Check path parameter
  const pathCobrand = getCobrandFromPathParam();
  if (isValidCobrand(pathCobrand)) {
    return pathCobrand as CobrandIdentifier;
  }

  // Priority 3: Default to Wellframe
  return CobrandIdentifier.WELLFRAME;
};

/**
 * Generates cobrand path with subpath support.
 * When hostname-based cobrand is detected, returns base subpath without cobrand.
 * When path-based, returns subpath with cobrand.
 * @param cobrandFromPath - Optional cobrand to include in path
 * @returns Generated path string
 */
export const getCobrandPathWithSubpath = (cobrandFromPath?: CobrandIdentifier): string => {
  const baseSubPath = getBaseSubPath();
  const cobrandFromHost = getCobrandFromHostName();

  if (baseSubPath) {
    if (cobrandFromHost) {
      return baseSubPath; // No cobrand in path when using hostname
    }
    if (cobrandFromPath) {
      return `${baseSubPath}/${cobrandFromPath}`;
    }
    return baseSubPath;
  }

  if (cobrandFromPath) {
    return `/${cobrandFromPath}`;
  }

  return '';
};
